/**
 * Server-Side Plugin
 *
 * Sends events to the backend for:
 * - Meta CAPI (server-side, deduped with client)
 * - GA4 Measurement Protocol (only for SubmitApplication)
 * - Applicant persistence
 * - Future platforms (LinkedIn, TikTok)
 *
 * Only fires for conversion events, not PageView spam.
 */

import { serverSideLog as log, getTraceId, TRACE_ID_HEADER } from "../../logger";
import { SERVER_SIDE_EVENTS } from "../registry";
import type { EventName, PluginResult, UnifiedEvent } from "../types";
import { BasePlugin } from "./base";

// Hash helper using Web Crypto API
async function sha256(str: string): Promise<string> {
  if (typeof window === "undefined" || !window.crypto?.subtle) {
    return str; // Fallback - server will hash
  }
  const encoder = new TextEncoder();
  const data = encoder.encode(str.toLowerCase().trim());
  const hash = await crypto.subtle.digest("SHA-256", data);
  return Array.from(new Uint8Array(hash))
    .map(b => b.toString(16).padStart(2, "0"))
    .join("");
}

export class ServerSidePlugin extends BasePlugin {
  name = "server-side";
  private initialized = false;

  async initialize(): Promise<void> {
    this.initialized = true;
  }

  supports(event: EventName): boolean {
    // Only send conversion events to server (not PageView spam)
    return SERVER_SIDE_EVENTS.includes(event);
  }

  isReady(): boolean {
    return typeof window !== "undefined" && this.initialized;
  }

  async track(event: UnifiedEvent): Promise<PluginResult> {
    if (!this.isReady()) {
      return this.error("not_ready");
    }

    try {
      // Hash PII before sending to server
      const hashedContext = await this.hashContext(event.context);

      // Build payload for backend
      const payload = {
        eventId: event.eventId,
        eventName: event.eventName,
        timestamp: event.timestamp,
        params: event.params,
        attribution: event.attribution,
        context: hashedContext,
      };

      // Send to backend analytics endpoint with trace ID for correlation
      const response = await fetch("/api/v1/analytics/track", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          [TRACE_ID_HEADER]: getTraceId(),
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        log.error(`HTTP error: ${response.status}`, undefined, { errorText });
        return this.error(`HTTP ${response.status}`);
      }

      const result = await response.json();

      log.info(`Sent: ${event.eventName}`, {
        eventId: event.eventId,
        status: result.status,
      });

      return this.success(result);
    } catch (err) {
      log.error("Send failed", err);
      return this.error(err instanceof Error ? err.message : "unknown_error");
    }
  }

  /**
   * Hash PII in context before sending to server.
   * Phone is NOT hashed here — backend handles Swiss normalization for consistent hashes.
   */
  private async hashContext(
    context?: UnifiedEvent["context"]
  ): Promise<UnifiedEvent["context"] | undefined> {
    if (!context) return undefined;

    const hashed = { ...context };

    // Hash email if not already hashed
    if (context.userEmail && !context.hashedEmail) {
      hashed.hashedEmail = await sha256(context.userEmail);
    }

    // Phone: pass raw to backend for Swiss normalization (don't hash here)

    // Hash name → first/last
    if (context.userName) {
      const parts = context.userName.trim().split(/\s+/);
      const firstName = parts[0] || "";
      const lastName = parts.slice(1).join(" ") || "";
      if (firstName) hashed.hashedFirstName = await sha256(firstName);
      if (lastName) hashed.hashedLastName = await sha256(lastName);
    }

    // Hash city
    if (context.userCity) {
      hashed.hashedCity = await sha256(context.userCity);
    }

    // Hash zip (digits only)
    if (context.userZip) {
      hashed.hashedZip = await sha256(context.userZip.replace(/\D/g, ""));
    }

    // Hash birth year
    if (context.userYear) {
      hashed.hashedDob = await sha256(context.userYear);
    }

    // Country: always Switzerland for this product
    hashed.hashedCountry = await sha256("ch");

    return hashed;
  }
}
